import { useEffect, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

const endpoint = "/api/purchasing/marketplace-favourites";
const validIds = (ids) => Array.isArray(ids) && ids.every((id) => Number.isSafeInteger(id) && id > 0);

export default function useMarketplaceFavourites({ userId, businessUnitId, enabled }) {
  const [ids, setIds] = useState([]), [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null), [version, setVersion] = useState(0);
  const [pending, setPending] = useState([]);
  const inFlight = useRef(new Set()), mounted = useRef(false);
  const current = () => {
    const user = readStoredUser();
    return mounted.current && String(user?.id) === String(userId) && String(user?.active_business_unit_id) === String(businessUnitId);
  };
  const config = () => ({ headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) }, pilcoreExpectedUserId: userId });
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!enabled || !userId || !businessUnitId) return undefined;
    let cancelled = false;
    const active = () => !cancelled && current();
    setLoaded(false); setError(null);
    const load = async () => {
      try {
        const { data } = await axios.get(endpoint, config());
        if (!validIds(data?.item_ids)) throw new Error("Invalid favourites response");
        if (!active()) return;
        const saved = new Set(data.item_ids);
        setIds([...saved]);
        // Import this user's previous browser-only selection once. Removing the
        // legacy key after success prevents old favourites reappearing later.
        const key = `pilcore:marketplace:favourites:${userId}:${businessUnitId}`;
        let legacy;
        try { legacy = JSON.parse(localStorage.getItem(key)); } catch { /* Invalid legacy data is ignored. */ }
        if (validIds(legacy)) for (const id of new Set(legacy)) {
          if (!active()) return;
          if (saved.has(id)) continue;
          try {
            await axios.post(`${endpoint}/${id}`, {}, config());
            saved.add(id);
          } catch (err) {
            // Deleted or no longer permitted items cannot be imported.
            if (![403, 404].includes(err.response?.status)) throw err;
          }
        }
        if (!active()) return;
        try { localStorage.removeItem(key); } catch { /* Server remains authoritative. */ }
        setIds([...saved]); setLoaded(true);
      } catch (_error) { if (active()) setError("load"); }
    };
    load();
    return () => { cancelled = true; };
    // Each workspace remounts when the authenticated user or business unit changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, businessUnitId, enabled, version]);

  const toggle = async (id) => {
    if (!loaded || !current() || inFlight.current.has(id)) return;
    const adding = !ids.includes(id);
    inFlight.current.add(id); setPending([...inFlight.current]); setError(null);
    setIds((previous) => adding ? [...previous, id] : previous.filter((entry) => entry !== id));
    try {
      if (adding) await axios.post(`${endpoint}/${id}`, {}, config());
      else await axios.delete(`${endpoint}/${id}`, config());
    } catch (_error) {
      if (current()) {
        setIds((previous) => adding ? previous.filter((entry) => entry !== id) : [...previous, id]);
        setError("save");
      }
    } finally {
      inFlight.current.delete(id);
      if (current()) setPending([...inFlight.current]);
    }
  };
  return { ids, loaded, error, toggle, pending, retry: () => setVersion((value) => value + 1) };
}
